import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Row,
  Col,
  Select,
  Table,
  Space,
  DatePicker,
  Input,
  Card,
  Divider,
  Modal,
  Spin,
  Upload,
} from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'

import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import {
  DeleteDischargeData,
  getChildrenList,
  GetDischargeData,
  GetDischargeDataById,
  IMG_URL,
  saveDischargeData,
  updateDischargeData,
} from '../APIComponents/AdminApi'
import axiosInstance from '../../Utils/axiosInstance'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'

const { Option } = Select
const { TextArea } = Input

const DischargeForm = () => {
  //============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Discharge Form')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1

  //============================================

  const [form] = Form.useForm()
  const [childrenList, setChildrenList] = useState([])
  const [dischargeList, setDischargeList] = useState([])

  const [loading, setLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [recordId, setRecordId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewData, setViewData] = useState(null)
  const [namunaFile, setNamunaFile] = useState(null)
  const [fileList, setFileList] = useState([])

  const handleView = (record) => {
    setViewData(record)
    setViewModalVisible(true)
  }

  useEffect(() => {
    fetchChildren()
    fetchDischargeList()
  }, [])

  const handleNamunaUpload = (info) => {
    const file = info.fileList?.[0]?.originFileObj

    if (file) {
      setNamunaFile(file)
    } else {
      setNamunaFile(null)
    }
  }

  // ================= Fetch Children =================
  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)
      setChildrenList(res.data)
    } catch {
      toast.error('Failed to fetch children list')
    }
  }

  // ================= Fetch Discharge Records =================
  const fetchDischargeList = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetDischargeData)
      setDischargeList(res.data)
    } catch {
      toast.error('Failed to fetch discharge list')
    } finally {
      setTableLoading(false)
    }
  }

  // ================= EDIT =================
  const handleEdit = async (record) => {
    try {
      setEditLoading(true)

      const data = record

      setShowForm(true)
      setIsEdit(true)
      setRecordId(data.reg_id)

      const exists = childrenList.some((child) => child.id === data.reg_id)

      if (!exists) {
        setChildrenList((prev) => [
          ...prev,
          {
            id: data.reg_id,
            fname: data.student?.fname,
            mname: data.student?.mname,
            lname: data.student?.lname,
            reg_no: data.student?.reg_no,
          },
        ])
      }

      form.setFieldsValue({
        childName: data.reg_id,
        dischargeDate: dayjs(data.date),
        dischargeReason: data.reason,

        namunaFile: data.namuna_twenty
          ? [
              {
                uid: '-1',
                name: data.namuna_twenty,
                status: 'done',
                url: IMG_URL + data.namuna_twenty,
              },
            ]
          : [],
      })
    } catch (error) {
      console.error(error)
    } finally {
      setEditLoading(false)
    }
  }

  // ================= Submit / Update =================
  const handleFinish = async (values) => {
    try {
      setLoading(true)

      const formData = new FormData()

      formData.append('reg_id', values.childName)
      formData.append('date', values.dischargeDate.format('YYYY-MM-DD'))
      formData.append('reason', values.dischargeReason)

      const appendFile = (values, field, key, formData) => {
        if (!values[field] || values[field].length === 0) return

        const file = values[field][0]

        //  New file uploaded
        if (file.originFileObj) {
          formData.append(key, file.originFileObj)
        }
        //  Existing file (unchanged) → send SAME key
        else if (file.url) {
          const existingFileName = file.url.replace(IMG_URL, '')
          formData.append(key, existingFileName)
        }
      }

      appendFile(values, 'namunaFile', 'namuna_twenty', formData)

      let res

      if (isEdit) {
        res = await axiosInstance.put(
          `${updateDischargeData}/${recordId}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        )

        if (res.data === 'UPDATED') {
          toast.success('Discharge Updated Successfully!')
        }
      } else {
        res = await axiosInstance.post(saveDischargeData, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        if (res.data === 'DISCHARGE') {
          toast.success('Discharge Saved Successfully!')
        } else if (res.data === 'ALREADYEXIST') {
          toast.warn('Already discharged!')
          return
        }
      }

      form.resetFields()
      setNamunaFile(null)
      setShowForm(false)
      setIsEdit(false)
      setRecordId(null)
      fetchDischargeList()
      fetchChildren()
    } catch {
      toast.error(isEdit ? 'Update failed' : 'Save failed')
    } finally {
      setLoading(false)
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeleteDischargeData}/${deleteId}`,
      )
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteModal(false)
        fetchDischargeList()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found!')
      }
    } catch (err) {
      toast.error('Delete failed')
    }
  }

  const filteredData = dischargeList.filter((item) => {
    const search = searchText.trim().toLowerCase()
    if (!search) return true

    const fullName = `
    ${item.student?.fname || ''}
    ${item.student?.mname || ''}
    ${item.student?.lname || ''}
  `
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()

    return (
      fullName.includes(search) || item.reason?.toLowerCase().includes(search)
    )
  })

  // ================= Table Columns =================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Child Name',
      render: (_, record) =>
        `${record.student?.fname || ''} ${record.student?.mname || ''} ${
          record.student?.lname || ''
        }`,
    },
    {
      title: 'Discharge Date',
      dataIndex: 'date',
      render: (date) => dayjs(date).format('DD-MM-YYYY'),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  // ================= File Validation =================
  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }

  return (
    <>
      <AdminHeader />
      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Discharge Form</h3>
          </div>

          {!showForm && (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={10} lg={14}></Col>
                <Col
                  xs={24}
                  md={6}
                  lg={4}
                  className="text-end px-md-2 pb-2 pb-md-0"
                >
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setShowForm(true)
                        form.resetFields()
                        setIsEdit(false)
                      }}
                    >
                      Add
                    </Button>
                  )}
                </Col>
                <Col xs={24} md={6} lg={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                bordered
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                loading={tableLoading}
                className="custom-table shadow rounded border"
                style={{ overflowX: 'auto' }}
              />
            </>
          )}

          {/* ================= FORM ================= */}
          {showForm && (
            <Spin spinning={editLoading}>
              <div className="row student-form-container rounded shadow  py-2">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  className="mb-4"
                >
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={10}>
                      <Form.Item
                        label="Select Children Name"
                        name="childName"
                        rules={[{ required: true }]}
                      >
                        <Select
                          disabled={isEdit}
                          showSearch
                          placeholder="Search by name / reg no"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={7}>
                      <Form.Item
                        label="Discharge Date"
                        name="dischargeDate"
                        rules={[{ required: true }]}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="DD-MM-YYYY"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={7}>
                      <Form.Item
                        label="Discharge Reason"
                        name="dischargeReason"
                        rules={[{ required: true }]}
                      >
                        <TextArea rows={1} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={7}>
                      <Form.Item
                        label="Upload Namuna 20"
                        name="namunaFile"
                        valuePropName="fileList"
                        getValueFromEvent={(e) =>
                          Array.isArray(e) ? e : e?.fileList
                        }
                        rules={[{ required: true }]}
                      >
                        <Upload
                          beforeUpload={validateFile}
                          accept=".jpg,.jpeg,.png,.pdf"
                          maxCount={1}
                          listType="picture"
                          style={{ width: '100%' }}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            style={{ width: '100%' }}
                          >
                            Upload (Max 3MB)
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Row justify="end" className="pb-3 pt-3">
                    <Space>
                      <Button
                        onClick={() => {
                          setShowForm(false)
                          form.resetFields()
                          setIsEdit(false)
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>

          {/* ===== VIEW MODAL ===== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Discharge Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            style={{
              top: 20, // space from top
              paddingBottom: 50, // space from bottom
            }}
            centered
            bodyStyle={{
              maxHeight: '75vh', // control height
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: 10,
            }}
          >
            {viewData && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px 5px',
                  borderRadius: '8px',
                }}
              >
                {/* Student Name */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Student Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.student?.fname || ''}{' '}
                      {viewData.student?.mname || ''}{' '}
                      {viewData.student?.lname || ''}
                    </div>
                  </Col>
                </Row>

                {/* Registration No */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Registration No :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.student?.reg_no || '-'}
                    </div>
                  </Col>
                </Row>

                {/* Discharge Date */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Discharge Date :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {dayjs(viewData.date).format('DD-MM-YYYY')}
                    </div>
                  </Col>
                </Row>

                {/* Reason */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Discharge Reason :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData.reason}</div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Namuna 20 File :</strong>
                  </Col>
                  <Col span={18}>
                    {viewData.namuna20 ? (
                      <a
                        href={`${IMG_URL}/${viewData.namuna20}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View File
                      </a>
                    ) : (
                      '-'
                    )}
                  </Col>
                </Row>
              </div>
            )}
          </Modal>
        </div>
      </div>
      <AdminFooter />
    </>
  )
}

export default DischargeForm
